import { Injectable, Injector } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ToastService } from 'src/app/shared/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../modules/auth';
import { refresh as ssoRefresh } from '../auth/sso';
import { environment } from 'src/environments/environment';

/**
 * Attaches a fresh bearer token to every portal API call and turns API errors
 * into toasts. A 401 means the session is gone → sign out.
 */
@Injectable()
export class CommonInterceptorInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private toastr: ToastService,
    private injector: Injector,
  ) {}

  // Resolved lazily: TranslateService depends on HttpClient (loader), and this
  // interceptor is part of the HttpClient pipeline — direct injection is circular.
  private get translate(): TranslateService {
    return this.injector.get(TranslateService);
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!request.url.startsWith(environment.apiUrl)) {
      return next.handle(request);
    }

    return from(this.resolveToken()).pipe(
      switchMap((token) => next.handle(token ? this.withToken(request, token) : request)),
      catchError((error: HttpErrorResponse) => {
        this.handleError(error, request);
        return throwError(() => error);
      })
    );
  }

  private async resolveToken(): Promise<string | undefined> {
    const refreshed = await ssoRefresh(30);
    if (refreshed) return refreshed;
    const stored = localStorage.getItem('authToken');
    return stored ? stored.replace(/"/g, '') : undefined;
  }

  private withToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({
      setHeaders: { accept: 'application/json', Authorization: `Bearer ${token}` },
    });
  }

  private handleError(error: HttpErrorResponse, request: HttpRequest<unknown>): void {
    if (error.status === 401) {
      this.authService.logout();
      return;
    }
    // `/me` is probed at boot; its failures are handled by the caller.
    if (request.url.endsWith('/me')) return;

    const apiMessage = (error.error && typeof error.error === 'object') ? error.error['error'] : undefined;
    const message = typeof apiMessage === 'string' && apiMessage.length > 0
      ? apiMessage
      : this.translate.instant('COMMON.ERROR_GENERIC');
    this.toastr.error(message);
  }
}
